// eslint-disable-next-line no-use-before-define
import React, { useEffect, useContext, useRef } from 'react';
import { useFormikContext, FormikProps } from 'formik';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useRouteMatch, useLocation } from 'react-router-dom';

import { GET_DIAGNOSIS_INTAKE } from './DiagnosisPages/Intake/Queries';
import { codeToIntakePageMapping } from './DiagnosisPages/Utils';
import { CarePlanActionType } from './Interface';
import ROPatientCarePlanContext from './ROPatientCarePlanContext';
import { GET_INTAKE_CODE_MAPPING } from './Queries';
import { GET_DIAGNOSIS_FROM_CAREPLAN } from './SidePanel/SidePanelQueries';
import {
  GET_ORDERED_DIAGNOSIS_LIST,
  GET_ICD10_CATEGORY_LIST,
  GET_CLINEX_CODES,
  GET_DIAGNOSIS,
} from './DiagnosisPages/Queries';
import { LOAD_TREATMENT_LIST_DATA, LOAD_TREATMENTINFO } from './TreatmentInfoPage/Queries';
import { LIST_OPTIONS } from './TreatmentInfoPage/TreatmentInfoPage';
import ROPatientCarePlanPageRouter from './Router';
import { CAREPLAN_PAGES } from './Constants';
import { UserContext } from 'op-contexts/index';

const CareplanApollo = (): JSX.Element => {
  const match = useRouteMatch<any>();
  const location = useLocation();
  const { values, setFieldValue }: FormikProps<any> = useFormikContext();
  const { hasFeature } = useContext(UserContext);
  const isAIPrepopulateEnabled = hasFeature('AI Prepopulate');

  const { diagnosis, intakePage } = values;
  const { dispatch } = useContext(ROPatientCarePlanContext);
  const { id: patientId, careplanId } = match.params;
  const isPrimaryDiagnosis = location.pathname.includes('primary');
  const isOnTreatmentInfoPage = location.pathname.includes(CAREPLAN_PAGES.TREATMENT_INFO);

  const [prefetchOrderedDiagnosisList] = useLazyQuery(GET_ORDERED_DIAGNOSIS_LIST, {
    fetchPolicy: 'cache-first',
  });

  const [prefetchIcd10CategoryList] = useLazyQuery(GET_ICD10_CATEGORY_LIST, {
    fetchPolicy: 'cache-first',
  });

  const [prefetchClinexCodes] = useLazyQuery(GET_CLINEX_CODES, {
    fetchPolicy: 'cache-first',
  });

  const [prefetchTreatmentListData] = useLazyQuery(LOAD_TREATMENT_LIST_DATA, {
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const prefetchQueries = () => {
      prefetchIcd10CategoryList({ variables: { isPrimaryDiagnosis } });
      prefetchTreatmentListData({
        variables: {
          listCategory: [
            LIST_OPTIONS.CHEMO_WHERE,
            LIST_OPTIONS.INTENT,
            LIST_OPTIONS.TREATING_DEPARTMENT,
            LIST_OPTIONS.CHEMO_SEQUENCING,
            LIST_OPTIONS.PREVIOUS_MDT,
            LIST_OPTIONS.BRACHY_WHERE,
            LIST_OPTIONS.BRACHY_SEQUENCING,
          ],
          patientId,
        },
      });

      if (careplanId !== 'create') {
        prefetchOrderedDiagnosisList({
          variables: { isPrimaryDiagnosis, patientId },
        });
        prefetchClinexCodes({
          variables: { patientId, careplanId },
        });
      }
    };

    let timeoutId: number | undefined;
    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const idleCallbackId =
      idleWindow.requestIdleCallback?.(prefetchQueries, {
        timeout: 1500,
      }) ?? null;

    if (idleCallbackId === null) {
      timeoutId = window.setTimeout(prefetchQueries, 0);
    }

    return () => {
      if (idleCallbackId !== null) {
        idleWindow.cancelIdleCallback?.(idleCallbackId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    careplanId,
    isPrimaryDiagnosis,
    patientId,
    prefetchClinexCodes,
    prefetchIcd10CategoryList,
    prefetchOrderedDiagnosisList,
    prefetchTreatmentListData,
  ]);

  const { data: dxFromCpData } = useQuery(GET_DIAGNOSIS_FROM_CAREPLAN, {
    fetchPolicy: 'cache-and-network',
    variables: { careplanId: careplanId },
    skip: !careplanId || careplanId === 'create',
  });

  const splitPathname = location.pathname.split('/');
  const diagnosisIndex = splitPathname.indexOf('diagnosis');
  const diagnosisIdFromPath = diagnosisIndex > 0 ? splitPathname[diagnosisIndex + 1] : null;

  const diagnosisId =
    diagnosisIdFromPath && diagnosisIdFromPath !== 'create'
      ? diagnosisIdFromPath
      : dxFromCpData
      ? dxFromCpData?.careplan?.diagnosis?.id
      : null;

  const primaryDiagnosisCode = dxFromCpData?.careplan?.diagnosis?.relatedPrimaryDiagnosis
    ? dxFromCpData?.careplan?.diagnosis?.relatedPrimaryDiagnosis?.diagnosisCode?.diagnosisCode
    : dxFromCpData?.careplan?.diagnosis?.code;

  useQuery(GET_DIAGNOSIS, {
    variables: { diagnosisId },
    fetchPolicy: 'cache-and-network',
    skip: !diagnosisId || diagnosisId === 'create',
    onCompleted: (data: any) => setFieldValue('diagnosis', data?.diagnosis),
  });

  const { data: intakeData, refetch: refetchIntake } = useQuery(GET_DIAGNOSIS_INTAKE, {
    variables: { diagnosisId: diagnosis?.id },
    skip: !diagnosis?.id || diagnosis?.id === 'create',
    fetchPolicy: 'cache-and-network',
  });

  const { data: intakeCodeMapping } = useQuery(GET_INTAKE_CODE_MAPPING, {
    fetchPolicy: 'cache-and-network',
  });

  useQuery(LOAD_TREATMENTINFO, {
    variables: {
      id: careplanId,
      forTreatmentInfoPrepopulate: isAIPrepopulateEnabled,
    },
    skip: !careplanId || careplanId === 'create' || !isOnTreatmentInfoPage,
    fetchPolicy: 'cache-and-network',
    onCompleted: (data: any) => {
      const result = data?.careplan;
      if (!result?.id) return;
      setFieldValue('treatmentInfo', result);
      if (isAIPrepopulateEnabled && result.treatmentInfoPrepopulate) {
        setFieldValue('treatmentInfoPrepopulate', result.treatmentInfoPrepopulate);
      }
    },
  });

  // When intake data changes, update the intake page
  useEffect(() => {
    if (intakeData) {
      setFieldValue('intake', intakeData?.intake);
    }
  }, [intakeData]);

  useEffect(() => {
    if (diagnosis && diagnosis.id && intakePage) {
      // Check if intake data doesn't exist for current intake page OR if intake data exists but is for a different diagnosis
      refetchIntake();
    }
  }, [primaryDiagnosisCode, intakePage, careplanId, setFieldValue]);

  useEffect(() => {
    if (intakeCodeMapping?.intakeCodeMapping?.length > 0 && diagnosis) {
      const codeToStream = intakeCodeMapping.intakeCodeMapping.reduce((current: any, candidate: any): any => {
        const name = candidate.intake.name;
        const code = candidate.diagnosisCode.diagnosisCode;
        current[code] = name;
        return current;
      }, {});
      dispatch({ type: CarePlanActionType.SET_INTAKE_CODE_MAPPING, payload: { ...codeToStream } });

      const primaryDiagnosis = diagnosis?.isPrimaryDiagnosis ? diagnosis : diagnosis?.relatedPrimaryDiagnosis;
      const intakeNameMapped = codeToIntakePageMapping(
        primaryDiagnosis?.diagnosisCode ? primaryDiagnosis?.diagnosisCode?.diagnosisCode : '',
        {
          ...codeToStream,
        },
      );
      setFieldValue('intakePage', intakeNameMapped);
    }
  }, [diagnosis, intakeCodeMapping, dispatch]);

  // Refetch intake data when diagnosis changes
  useEffect(() => {
    if (diagnosis?.id && refetchIntake) {
      refetchIntake();
    }
  }, [diagnosis?.id, refetchIntake]);

  return <ROPatientCarePlanPageRouter />;
};

export default CareplanApollo;
